import React from "react";
import { getUserAvatarUrl } from "services/user-avatar.service";

interface UserAvatarProps {
  companyId: string;
  user: { id: string; name?: string | null; avatarFileId?: string | null };
  size?: number;
  online?: boolean;
  /** When > 0, draws a red unread badge in the top-right corner. */
  unread?: number;
  rounded?: "lg" | "md" | "full" | "none";
  onClick?: () => void;
}

const ROUND_CLASS: Record<NonNullable<UserAvatarProps["rounded"]>, string> = {
  lg: "rounded-lg",
  md: "rounded-md",
  full: "rounded-full",
  none: "",
};

// Stable colour from userId hash for the initials fallback
function colorFor(seed: string): string {
  let hash = 0;
  for (let i = 0; i < seed.length; i++) {
    hash = (hash * 31 + seed.charCodeAt(i)) | 0;
  }
  const palette = [
    "#1e40af", "#0f766e", "#7c2d12", "#6d28d9", "#0e7490",
    "#9d174d", "#166534", "#92400e", "#1e3a8a", "#3f6212",
  ];
  return palette[Math.abs(hash) % palette.length];
}

const UserAvatar: React.FC<UserAvatarProps> = ({
  companyId,
  user,
  size = 40,
  online = false,
  unread = 0,
  rounded = "lg",
  onClick,
}) => {
  const initial = (user.name || "?").trim().charAt(0).toUpperCase();
  const bg = colorFor(user.id || "x");
  const dotSize = Math.max(8, Math.round(size * 0.22));

  const inner = user.avatarFileId ? (
    <img
      src={getUserAvatarUrl(companyId, user.avatarFileId)}
      alt={user.name || ""}
      className="w-full h-full object-cover"
      draggable={false}
    />
  ) : (
    <span
      className="text-white font-semibold flex items-center justify-center w-full h-full"
      style={{ backgroundColor: bg, fontSize: Math.round(size * 0.45) }}
    >
      {initial}
    </span>
  );

  return (
    <span
      onClick={onClick}
      className={`relative inline-block overflow-visible ${onClick ? "cursor-pointer" : ""}`}
      style={{ width: size, height: size }}
      title={user.name || undefined}
    >
      <span
        className={`block overflow-hidden bg-gray-200 ${ROUND_CLASS[rounded]}`}
        style={{ width: size, height: size }}
      >
        {inner}
      </span>
      {online && (
        <span
          className="absolute bg-green-500 ring-2 ring-gray-900 animate-pulse"
          style={{
            width: dotSize,
            height: dotSize,
            borderRadius: "9999px",
            right: -dotSize * 0.15,
            bottom: -dotSize * 0.15,
          }}
        />
      )}
      {unread > 0 && (
        <span
          className="absolute bg-red-500 text-white font-semibold flex items-center justify-center ring-2 ring-gray-900"
          style={{
            minWidth: Math.max(16, Math.round(size * 0.4)),
            height: Math.max(16, Math.round(size * 0.4)),
            padding: "0 4px",
            borderRadius: "9999px",
            top: -Math.round(size * 0.1),
            right: -Math.round(size * 0.1),
            fontSize: Math.max(9, Math.round(size * 0.26)),
            lineHeight: 1,
          }}
        >
          {unread > 99 ? "99+" : unread}
        </span>
      )}
    </span>
  );
};

export default UserAvatar;
